import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Navigation, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCountry } from "@/contexts/CountryContext";

const STORAGE_KEY = "recomendalibros:postal_code";
const NEAREST_KEY = "recomendalibros:nearest_store";

/**
 * Per-card CTA: "Your nearest local bookshop".
 *
 * If the user hasn't shared their postal code yet, the CTA shows an inline
 * form asking for it. Once entered, we look up bookstores via OpenStreetMap
 * and persist the pick to localStorage / sessionStorage so subsequent cards
 * render instantly.
 */
export default function LocalBookshopCTA({ book, testIdSuffix = "" }) {
    const { t } = useTranslation();
    const { country } = useCountry();
    const [nearest, setNearest] = useState(null);
    const [askingPostal, setAskingPostal] = useState(false);
    const [postalDraft, setPostalDraft] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        try {
            const cached = sessionStorage.getItem(NEAREST_KEY);
            if (cached) {
                setNearest(JSON.parse(cached));
                return () => { alive = false; };
            }
        } catch { /* ignore */ }

        const pc = (() => {
            try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
        })();
        if (!pc) return () => { alive = false; };

        (async () => {
            try {
                const { data } = await api.get("/bookstores/nearby", {
                    params: { postal_code: pc, country, limit: 1 },
                });
                const top = (data?.bookstores || [])[0];
                if (alive && top) {
                    setNearest(top);
                    try { sessionStorage.setItem(NEAREST_KEY, JSON.stringify(top)); } catch { /* ignore */ }
                }
            } catch { /* silent */ }
        })();
        return () => { alive = false; };
    }, [country]);

    const handlePostalSubmit = async (e) => {
        e.preventDefault();
        const pc = postalDraft.trim();
        if (pc.length < 3) {
            toast.error(t("local_bookshop.invalid_postal"));
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.get("/bookstores/nearby", {
                params: { postal_code: pc, country, limit: 1 },
            });
            const top = (data?.bookstores || [])[0];
            if (!top) {
                toast.error(t("local_bookshop.no_results"));
                return;
            }
            try { localStorage.setItem(STORAGE_KEY, pc); } catch { /* ignore */ }
            try { sessionStorage.setItem(NEAREST_KEY, JSON.stringify(top)); } catch { /* ignore */ }
            setNearest(top);
            setAskingPostal(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || t("local_bookshop.error"));
        } finally {
            setBusy(false);
        }
    };

    if (!nearest && askingPostal) {
        return (
            <form
                onSubmit={handlePostalSubmit}
                className="mt-3 flex items-center gap-2 border border-[var(--accent)] rounded-full pl-4 pr-1 py-1"
                data-testid={`local-bookshop-postal-form${testIdSuffix}`}
            >
                <MapPin size={13} strokeWidth={1.8} className="text-[var(--accent)] flex-shrink-0" />
                <input
                    autoFocus
                    value={postalDraft}
                    onChange={(e) => setPostalDraft(e.target.value)}
                    placeholder={t("local_bookshop.postal_placeholder")}
                    maxLength={12}
                    className="flex-1 bg-transparent border-0 outline-none text-sm text-[var(--ink)] py-1 min-w-0"
                />
                <button
                    type="submit"
                    disabled={busy}
                    data-testid={`local-bookshop-postal-submit${testIdSuffix}`}
                    className="bg-[var(--accent)] text-[var(--bg)] rounded-full px-3 py-1 text-[10px] uppercase tracking-widest disabled:opacity-50"
                >
                    {busy ? "…" : t("local_bookshop.postal_submit")}
                </button>
                <button
                    type="button"
                    onClick={() => setAskingPostal(false)}
                    className="text-[var(--ink-dim)] hover:text-[var(--ink)] p-1"
                    aria-label="Cancel"
                >
                    <X size={12} strokeWidth={1.8} />
                </button>
            </form>
        );
    }

    if (!nearest) {
        return (
            <button
                type="button"
                onClick={() => setAskingPostal(true)}
                data-testid={`local-bookshop-cta${testIdSuffix}`}
                className="mt-3 flex items-center gap-2.5 border border-dashed border-[var(--accent)]/60 hover:border-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] text-[var(--accent)] rounded-full px-4 py-2 transition-colors w-full"
            >
                <MapPin size={13} strokeWidth={1.8} className="flex-shrink-0" />
                <span className="text-xs uppercase tracking-widest text-left flex-1">
                    {t("local_bookshop.find_nearest")}
                </span>
            </button>
        );
    }

    const mapsQuery = `${nearest.name}${nearest.address ? ", " + nearest.address : ""}`;
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`;

    return (
        <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`local-bookshop-cta${testIdSuffix}`}
            onClick={() => {
                try {
                    window.recomendalibros?.trackClick?.({
                        store: "local_bookshop",
                        book_title: book.title,
                        book_author: book.author,
                        isbn: book.isbn || null,
                        bookshop_name: nearest.name,
                    });
                } catch { /* ignore */ }
            }}
            className="mt-3 flex items-center gap-2.5 border border-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] text-[var(--accent)] rounded-full px-4 py-2 transition-colors group"
            aria-label={`${t("local_bookshop.your_nearest")} ${nearest.name}`}
        >
            <MapPin size={13} strokeWidth={1.8} className="flex-shrink-0" />
            <div className="flex-1 min-w-0 text-left">
                <p className="text-[10px] uppercase tracking-[0.25em] leading-none mb-0.5">
                    {t("local_bookshop.your_nearest")}
                </p>
                <p className="text-xs leading-tight truncate">{nearest.name}</p>
            </div>
            <span
                className="text-[10px] uppercase tracking-widest opacity-70 inline-flex items-center gap-1 ml-2 flex-shrink-0"
            >
                <Navigation size={10} strokeWidth={1.8} /> {nearest.distance_km}km
            </span>
        </a>
    );
}
